import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { IconCalendarPlus, IconPencil, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import {
  type Holiday,
  createHoliday,
  deleteHoliday,
  fetchHolidays,
  updateHoliday,
} from '../../shared/api/holidays'
import { useAuthStore } from '../auth/store'
import { isApiError } from '../../shared/api/client'

const NOW_YEAR = new Date().getFullYear()
const YEARS = [NOW_YEAR - 1, NOW_YEAR, NOW_YEAR + 1, NOW_YEAR + 2].map((y) => String(y))

function fmtDate(s: string): string {
  const [y, m, d] = s.split('-')
  const wd = new Date(`${s}T12:00:00`).toLocaleDateString('ru-RU', { weekday: 'short' })
  return `${d}.${m}.${y}, ${wd}`
}

export function HolidaysTab() {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()

  const [year, setYear] = useState(String(NOW_YEAR))
  const listQ = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => fetchHolidays(Number(year)),
  })

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Holiday | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null)

  const remove = useMutation({
    mutationFn: (date: string) => deleteHoliday(date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holidays'] })
      notifications.show({ color: 'teal', title: 'Удалено', message: 'Дата удалена из календаря' })
      setDeleteTarget(null)
    },
    onError: () => notifications.show({ color: 'red', title: 'Ошибка', message: 'Не удалось удалить' }),
  })

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Select
          label="Год"
          data={YEARS}
          value={year}
          onChange={(v) => setYear(v ?? String(NOW_YEAR))}
          allowDeselect={false}
          w={140}
        />
        {isAdmin && (
          <Button
            leftSection={<IconCalendarPlus size={16} />}
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
          >
            Добавить дату
          </Button>
        )}
      </Group>

      {listQ.isLoading && (
        <Center mih={160}>
          <Loader />
        </Center>
      )}

      {listQ.data && listQ.data.length === 0 && (
        <Card withBorder p="xl" radius="md">
          <Center>
            <Text c="dimmed" size="sm">
              На {year} год календарь пуст
            </Text>
          </Center>
        </Card>
      )}

      {listQ.data && listQ.data.length > 0 && (
        <Card withBorder p={0} radius="md">
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Дата</Table.Th>
                <Table.Th>Название</Table.Th>
                <Table.Th>Тип</Table.Th>
                {isAdmin && <Table.Th w={1} />}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {listQ.data.map((h) => (
                <Table.Tr key={h.date}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(h.date)}</Table.Td>
                  <Table.Td>{h.name}</Table.Td>
                  <Table.Td>
                    {h.is_workday ? (
                      <Badge color="orange" variant="light">
                        Отработка
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        Нерабочий
                      </Badge>
                    )}
                  </Table.Td>
                  {isAdmin && (
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => {
                            setEditing(h)
                            setFormOpen(true)
                          }}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(h)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <HolidayFormModal opened={formOpen} onClose={() => setFormOpen(false)} editing={editing} />

      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={<Text fw={700}>Удалить дату?</Text>}
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            «{deleteTarget?.name}» ({deleteTarget && fmtDate(deleteTarget.date)}) будет удалена из
            производственного календаря.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Отмена
            </Button>
            <Button
              color="red"
              loading={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.date)}
            >
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

function HolidayFormModal({
  opened,
  onClose,
  editing,
}: {
  opened: boolean
  onClose: () => void
  editing: Holiday | null
}) {
  const qc = useQueryClient()
  const [date, setDate] = useState('')
  const [name, setName] = useState('')
  const [isWorkday, setIsWorkday] = useState(false)

  useEffect(() => {
    if (!opened) return
    setDate(editing?.date ?? '')
    setName(editing?.name ?? '')
    setIsWorkday(editing?.is_workday ?? false)
  }, [opened, editing])

  const save = useMutation({
    mutationFn: () =>
      editing
        ? updateHoliday(editing.date, { name: name.trim(), is_workday: isWorkday })
        : createHoliday({ date, name: name.trim(), is_workday: isWorkday }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['holidays'] })
      notifications.show({ color: 'teal', title: editing ? 'Дата обновлена' : 'Дата добавлена', message: name.trim() })
      onClose()
    },
    onError: (e) => {
      const msg = isApiError(e)
        ? e.response?.status === 409
          ? 'Эта дата уже есть в календаре'
          : (e.response?.data.error.message ?? 'Не удалось сохранить')
        : 'Не удалось сохранить'
      notifications.show({ color: 'red', title: 'Ошибка', message: msg })
    },
  })

  const canSubmit = !!date && name.trim().length > 0

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      title={<Text fw={700}>{editing ? 'Редактировать дату' : 'Новая дата в календаре'}</Text>}
    >
      <Stack gap="md">
        <TextInput
          label="Дата"
          type="date"
          required
          value={date}
          disabled={!!editing}
          description={editing ? 'Дату изменить нельзя — удалите и создайте заново' : undefined}
          onChange={(e) => setDate(e.currentTarget.value)}
        />
        <TextInput
          label="Название"
          placeholder="Например: Навруз / Отработка"
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Switch
          label="Рабочий день (отработочная суббота)"
          description={
            isWorkday
              ? 'Этот день считается рабочим, даже если выпадает на выходной'
              : 'Нерабочий день — переносит дедлайны регулярок вперёд'
          }
          checked={isWorkday}
          onChange={(e) => setIsWorkday(e.currentTarget.checked)}
        />
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSubmit}>
            {editing ? 'Сохранить' : 'Добавить'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
